// # Dedicated Tasks Component for Step 1
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import DedicatedCalendar from './give_dedic1_cld';
import { 
  Box, Typography, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField,
  DialogContentText, Fab, Tooltip, Grid, Paper,
  Tabs, Tab, Card, CardContent, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  ReadMore as ReadMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { format, isToday, parseISO } from 'date-fns';
import './give_dedic1.css';
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager

// # Define task contexts
const TASK_CONTEXTS = {
  general: { label: 'General', color: '#3498db' },
  study: { label: 'Study', color: '#2ecc71' },
  practice: { label: 'Practice', color: '#f39c12' }
};

// # Define chapter list
const CHAPTERS = [
  "Allergy & Immunology",
  "Behavioral Science",
  "Biostatistics & Epidemiology",
  "Cardiovascular",
  "Dermatology",
  "Drug Advertising & Prescribing",
  "Emergency Medicine",
  "Endocrine, Diabetes & Metabolism",
  "Ethics & Legal",
  "Female Reproductive & Breast",
  "Gastrointestinal & Nutrition",
  "General Principles",
  "Geriatrics",
  "Health & Healthcare Systems",
  "Hematology & Oncology",
  "Infectious Diseases",
  "Male Reproductive",
  "Musculoskeletal, Skin & Connective Tissue",
  "Nervous System & Special Senses",
  "Patient Safety & Quality Improvement",
  "Pediatrics",
  "Pregnancy, Childbirth & Puerperium",
  "Preventive Medicine & Health Maintenance",
  "Psychiatry",
  "Pulmonary & Critical Care",
  "Renal, Urinary Systems & Electrolytes",
  "Surgery",
  "Random"
];

// # Helper function to generate task ID
const generateTaskId = () => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let uniqueId = '';
  for (let i = 0; i < 10; i++) {
    uniqueId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `ddc_tsk${timestamp}${uniqueId}`;
};

// # Initial form data with today's date
const initialFormData = {
  title: '',
  description: '',
  date: format(new Date(), 'yyyy-MM-dd'),
  context: 'general',
  chapter: '',
  qbank: ''
};

function DedicatedTasks1({ isAdmin }) {
  const { user } = useAuthStore();
  const [selectedTab, setSelectedTab] = useState(0);
  const [tasks, setTasks] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [calendarExpanded, setCalendarExpanded] = useState(true);
  const calendarRef = React.useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Add dark mode state
  // # Add state for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    taskId: null,
    taskTitle: ''
  });
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  // # Reset form data when dialog opens
  const handleOpenDialog = () => {
    setFormData({
      ...initialFormData,
      date: format(new Date(), 'yyyy-MM-dd') // Reset date to today
    });
    setOpenDialog(true);
  };

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load tasks from Firestore
  useEffect(() => {
    const loadTasks = async () => {
      if (!targetUserId) return;

      try {
        const docRef = doc(db, 's1_mentee', targetUserId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // # Get dedicated_tasks data or initialize empty object
          const dedicatedTasks = data.dedicated_tasks || {};
          setTasks(dedicatedTasks);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };

    loadTasks();
  }, [targetUserId]);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle form submit
  const handleSubmit = async () => {
    if (!formData.title || !formData.date || !targetUserId) return;

    try {
      const docRef = doc(db, 's1_mentee', targetUserId);
      const docSnap = await getDoc(docRef);
      
      let updatedTasks = {};
      if (docSnap.exists()) {
        const data = docSnap.data();
        updatedTasks = data.dedicated_tasks || {};
      }

      // Create task object with common fields
      const taskData = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        context: formData.context,
        completed: formData.isEditing ? formData.completed : false
      };

      // Add chapter and qbank if context is study or practice
      if (formData.context === 'study' || formData.context === 'practice') {
        taskData.chapter = formData.chapter;
        if (formData.context === 'practice') {
          taskData.qbank = formData.qbank;
        }
      }

      if (formData.isEditing && formData.taskId) {
        // Update existing task
        const existingTask = updatedTasks[formData.taskId];
        if (existingTask) {
          updatedTasks[formData.taskId] = {
            ...taskData,
            metadata: existingTask.metadata // Preserve original metadata
          };
        }
      } else {
        // Create new task
        const taskId = generateTaskId();
        updatedTasks[taskId] = {
          ...taskData,
          metadata: {
            createdAt: new Date().toISOString(),
            createdBy: user.email
          }
        };
      }

      await setDoc(docRef, {
        dedicated_tasks: updatedTasks
      }, { merge: true });

      setTasks(updatedTasks);
      setOpenDialog(false);
      setFormData(initialFormData); // Reset form
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  // # Handle task completion toggle
  const handleToggleComplete = async (taskId) => {
    if (!targetUserId || !isMentor) return;

    try {
      const updatedTasks = {
        ...tasks,
        [taskId]: {
          ...tasks[taskId],
          completed: !tasks[taskId].completed
        }
      };

      const docRef = doc(db, 's1_mentee', targetUserId);
      await setDoc(docRef, {
        dedicated_tasks: updatedTasks
      }, { merge: true });

      setTasks(updatedTasks);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // # Update handleDeleteTask to open confirmation dialog
  const handleDeleteTask = (taskId) => {
    if (!isMentor) return;
    const task = tasks[taskId];
    setDeleteConfirmation({
      open: true,
      taskId,
      taskTitle: task.title
    });
  };

  // # Add confirmDelete function
  const confirmDelete = async () => {
    if (!targetUserId || !isMentor) return;
    const taskId = deleteConfirmation.taskId;
    try {
      const docRef = doc(db, 's1_mentee', targetUserId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedTasks = { ...data.dedicated_tasks };
        delete updatedTasks[taskId];

        await setDoc(docRef, {
          dedicated_tasks: updatedTasks
        }, { merge: true });

        setTasks(updatedTasks);
        setOpenViewDialog(false);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
    setDeleteConfirmation({ open: false, taskId: null, taskTitle: '' });
  };

  // # Handle edit task
  const handleEditTask = (task) => {
    if (!isMentor) return;
    setFormData({
      title: task.title,
      description: task.description,
      date: task.date,
      context: task.context,
      chapter: task.chapter || '',
      qbank: task.qbank || '',
      completed: task.completed,
      isEditing: true,
      taskId: task.id
    });
    setOpenViewDialog(false);
    setOpenDialog(true);
  };

  // # Convert tasks object to array for display
  const tasksArray = Object.entries(tasks).map(([id, task]) => ({
    id,
    ...task
  }));

  // # Filter tasks based on selected tab
  const getFilteredTasks = () => {
    switch (selectedTab) {
      case 1: // Calendar view
        return tasksArray;
      default: // Tasks view
        return tasksArray;
    }
  };

  // # Handle task date update (for drag and drop)
  const handleUpdateTaskDate = async (taskId, newDate) => {
    if (!targetUserId || !isMentor) return;

    try {
      // # Get the current task
      const task = tasks[taskId];
      if (!task) return;

      // # Create updated task with new date
      const updatedTask = {
        ...task,
        date: newDate
      };

      // # Update tasks object
      const updatedTasks = {
        ...tasks,
        [taskId]: updatedTask
      };

      // # Update in Firestore
      const docRef = doc(db, 's1_mentee', targetUserId);
      await setDoc(docRef, {
        dedicated_tasks: updatedTasks
      }, { merge: true });

      // # Update local state
      setTasks(updatedTasks);
    } catch (error) {
      console.error('Error updating task date:', error);
    }
  };

  return (
    <div className={`gv_ddc1_container ${isDarkMode ? 'gv_ddc1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="gv_ddc1_content">
        <div className="gv_ddc1_header">
          <Typography variant="h4" component="h1" className="gv_ddc1_title">
            Dedicated Period Planning {isMentor && <span className="gv_ddc1_mentor_pill">Mentor</span>}
          </Typography>
          {isMentor && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDialog}
              className="gv_ddc1_add_button"
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            aria-label="dedicated tasks tabs"
          >
            <Tab label="Dedicated Tasks" />
            <Tab label="Calendar" />
          </Tabs>
        </Box>

        {/* Tasks View */}
        {selectedTab === 0 && (
          <List className="gv_ddc1_list">
            {tasksArray.length > 0 ? (
              tasksArray.map((task) => (
                <ListItem
                  key={task.id}
                  className={`gv_ddc1_item ${task.completed ? 'completed' : ''}`}
                >
                  {/* Left Section */}
                  <div className="gv_ddc1_item_left">
                    <Checkbox
                      checked={task.completed || false}
                      onChange={() => handleToggleComplete(task.id)}
                      className="gv_ddc1_checkbox"
                    />
                    <div className="gv_ddc1_title_date">
                      <Typography variant="subtitle1" component="h3" className="gv_ddc1_task_title">
                        {task.title}
                      </Typography>
                      <Typography variant="body2" className="gv_ddc1_task_date">
                        {format(new Date(task.date), 'MMM dd, yyyy')}
                      </Typography>
                    </div>
                  </div>

                  {/* Middle Section - Description */}
                  <div className="gv_ddc1_item_middle">
                    {task.description && (
                      <>
                        <Typography variant="body2" className="gv_ddc1_description">
                          {task.description.length > 35 || task.description.split(' ').length > 5 
                            ? `${task.description.slice(0, 35)}...` 
                            : task.description}
                        </Typography>
                        {(task.description.length > 35 || task.description.split(' ').length > 5) && (
                          <Tooltip title="Read More">
                            <IconButton
                              size="small"
                              className="gv_ddc1_read_more"
                              onClick={() => {
                                // Show description in dialog
                                setViewData(task);
                                setOpenViewDialog(true);
                              }}
                            >
                              <ReadMoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right Section - Context & Tags */}
                  <div className="gv_ddc1_item_right">
                    <div className="gv_ddc1_item_tags">
                      <Chip
                        label={TASK_CONTEXTS[task.context]?.label}
                        className="gv_ddc1_context_chip"
                        style={{
                          backgroundColor: TASK_CONTEXTS[task.context]?.color,
                          color: 'white'
                        }}
                      />
                      {task.chapter && (
                        <Chip
                          label={task.chapter}
                          className="gv_ddc1_chapter_chip"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {task.qbank && (
                        <Chip
                          label={task.qbank}
                          className="gv_ddc1_qbank_chip"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </div>
                    <div className="gv_ddc1_item_actions">
                      <Tooltip title="Edit Task">
                        <IconButton
                          size="small"
                          className="gv_ddc1_edit_button"
                          onClick={() => handleEditTask(task)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Task">
                        <IconButton
                          size="small"
                          className="gv_ddc1_delete_button"
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </ListItem>
              ))
            ) : (
              <Typography className="gv_ddc1_empty_message">
                No dedicated tasks found. Create tasks to track your dedicated period.
              </Typography>
            )}
          </List>
        )}

        {/* Calendar View */}
        {selectedTab === 1 && (
          <DedicatedCalendar 
            tasks={tasks} 
            onToggleComplete={handleToggleComplete}
            onViewTask={(task) => {
              setViewData(task);
              setOpenViewDialog(true);
            }}
            onUpdateDate={handleUpdateTaskDate}
            isMentor={isMentor}
          />
        )}

        {/* Create/Edit Task Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          className="gv_ddc1_dialog"
        >
          <DialogTitle>{formData.isEditing ? 'Edit Task' : 'Create Task'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Task Title"
              type="text"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Date"
              type="date"
              fullWidth
              className="gv_ddc1_date_field"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: format(new Date(), 'yyyy-MM-dd'), // Prevent past dates
              }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Context</InputLabel>
              <Select
                value={formData.context}
                onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }}
              >
                {Object.entries(TASK_CONTEXTS).map(([value, { label }]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(formData.context === 'study' || formData.context === 'practice') && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Chapter</InputLabel>
                <Select
                  value={formData.chapter}
                  onChange={(e) => setFormData(prev => ({ ...prev, chapter: e.target.value }))}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                >
                  {CHAPTERS.map((chapter) => (
                    <MenuItem key={chapter} value={chapter}>{chapter}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {formData.context === 'practice' && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Question Bank</InputLabel>
                <Select
                  value={formData.qbank}
                  onChange={(e) => setFormData(prev => ({ ...prev, qbank: e.target.value }))}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    }
                  }}
                >
                  <MenuItem value="UWorld">UWorld</MenuItem>
                  <MenuItem value="Amboss">Amboss</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              className="gv_ddc1_description_field"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              InputProps={{
                style: { height: 'auto' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {formData.isEditing ? 'Save Changes' : 'Create Task'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Task Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          className="gv_ddc1_view_dialog"
        >
          {viewData && (
            <>
              <DialogTitle className="gv_ddc1_view_dialog_header">
                <Typography variant="h6">{viewData.title}</Typography>
                <div className="gv_ddc1_view_dialog_actions">
                  <Tooltip title="Edit Task">
                    <IconButton
                      onClick={() => handleEditTask(viewData)}
                      size="small"
                      className="gv_ddc1_edit_button"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Task">
                    <IconButton
                      onClick={() => handleDeleteTask(viewData.id)}
                      size="small"
                      className="gv_ddc1_delete_button"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </DialogTitle>
              <DialogContent className="gv_ddc1_view_dialog_content">
                <div className="gv_ddc1_view_dialog_info">
                  <Typography variant="body2" className="gv_ddc1_view_dialog_date">
                    <TodayIcon fontSize="small" />
                    {format(new Date(viewData.date), 'MMMM dd, yyyy')}
                  </Typography>
                  <Chip
                    label={TASK_CONTEXTS[viewData.context]?.label}
                    className="gv_ddc1_view_context_chip"
                    style={{
                      backgroundColor: TASK_CONTEXTS[viewData.context]?.color,
                      color: 'white'
                    }}
                  />
                  {viewData.chapter && (
                    <Chip
                      label={viewData.chapter}
                      className="gv_ddc1_view_chapter_chip"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {viewData.qbank && (
                    <Chip
                      label={viewData.qbank}
                      className="gv_ddc1_view_qbank_chip"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </div>
                {viewData.description && (
                  <Typography className="gv_ddc1_view_dialog_description">
                    {viewData.description}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* # Add Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmation.open}
          onClose={() => setDeleteConfirmation({ open: false, taskId: null, taskTitle: '' })}
          className="gv_ddc1_delete_dialog"
        >
          <DialogTitle className="gv_ddc1_delete_dialog_title">
            <DeleteIcon className="gv_ddc1_delete_dialog_icon" />
            Confirm Delete
          </DialogTitle>
          <DialogContent className="gv_ddc1_delete_dialog_content">
            <DialogContentText>
              Are you sure you want to delete the task "{deleteConfirmation.taskTitle}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions className="gv_ddc1_delete_dialog_actions">
            <Button 
              onClick={() => setDeleteConfirmation({ open: false, taskId: null, taskTitle: '' })}
              className="gv_ddc1_delete_dialog_cancel"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete}
              color="error"
              variant="contained"
              className="gv_ddc1_delete_dialog_confirm"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default DedicatedTasks1;
